import {
  Button,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import humanparser from "humanparser";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import z from "zod";
import { useAlerts, useDialog } from "../../../providers";
import { AuthenticationInstance } from "../../../utils/authenticationClass";
import { DialogHeader } from "../../generics/dialog-header";
import { LoginDialog } from "./login-dialog";

type FormState = {
  email: string;
  username: string;
  name: string;
  password: string;
  confirmPassword: string;
  gender: "male" | "female" | "non-binary" | null;
  agree: boolean;
  agreedToMarketing: boolean;
  dob: DateTime | null;
};

type ErrorState = Partial<Record<keyof FormState, string | boolean>>;

const GENDER_OPTIONS = ["male", "female", "non-binary"];

const schema = z
  .object({
    email: z.string().email(),
    username: z.string().min(3),
    name: z.string(),
    password: z.string().min(8),
    confirmPassword: z.string(),
    gender: z.enum(GENDER_OPTIONS as any),
    agree: z.boolean(),
    agreedToMarketing: z.boolean(),
    dob: z.any(),
  })
  .refine((data: Partial<FormState>) => data.agree, {
    message: "You must agree to the terms of service and privacy policy",
    path: ["agree"],
  })
  .refine((data: Partial<FormState>) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  })
  .refine((data: Partial<FormState>) => /[^a-zA-Z\d\s:]/.test(data.password as string), {
    message: "Password must contain at least one special character",
    path: ["password"],
  })
  .refine((data: Partial<FormState>) => Math.abs(data?.dob?.diffNow("years")?.years ?? 0) >= 21, {
    message: "Must be at least 21 years old to register",
    path: ["dob"],
  })
  .refine(
    (data: Partial<FormState>) => {
      const parsed = humanparser.parseName(data.name as string);
      return parsed.firstName && parsed.lastName;
    },
    {
      message: "Please enter your full name",
      path: ["name"],
    },
  );

let handleLogin = () => {};

export const RegisterDialog = ({
  experienceId,
  cancel,
  ok,
  referralCode,
}: {
  cancel: () => void;
  ok: (result: any) => void;
  experienceId?: number;
  referralCode?: string;
}) => {
  const [state, setState] = useState<FormState>({
    email: "",
    username: "",
    name: "",
    password: "",
    confirmPassword: "",
    gender: null,
    agree: false,
    dob: null,
    agreedToMarketing: false,
  });

  const [errors, setErrors] = useState<ErrorState>({});
  const [loading, setLoading] = useState(false);
  const { showError } = useAlerts();
  const { open: openDialog } = useDialog();

  const generateFieldErrors = () => {
    const err = schema.safeParse(state).error;

    if (!err?.flatten) {
      // No errors, clear the error state
      return setErrors((curr) => Object.keys(curr).reduce((acc, key) => ({ ...acc, [key]: false }), {}));
    }

    const { fieldErrors = {} } = err.flatten();

    setErrors((curr: ErrorState) => {
      const keys: (keyof ErrorState)[] = Object.keys(curr) as (keyof ErrorState)[];
      if (keys.length === 0) {
        return curr;
      }

      return keys.reduce<ErrorState>((acc: ErrorState, key: keyof ErrorState) => {
        return {
          ...acc,
          //@ts-ignore
          [key]: fieldErrors?.[key]?.join(", ") ?? false,
        };
      }, {});
    });
  };

  // Validate the form state
  useEffect(() => {
    generateFieldErrors();
  }, [state]);

  // When the user blurs a field, allow it to show errors
  const handleBlur = (e: any) => {
    const field = e.target.name;
    if (errors?.[field as keyof ErrorState] !== undefined) return;

    setErrors((prev) => ({
      ...prev,
      [field]: false,
    }));
    generateFieldErrors();
  };

  const isValid = useMemo(() => schema.safeParse(state).success, [state]);

  const handleChange = (e: any) => {
    setState({
      ...state,
      [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  };

  handleLogin = () => {
    openDialog({
      component: LoginDialog,
    });
    cancel();
  };

  const handleSubmit = () => {
    if (!isValid) return;

    setLoading(true);

    AuthenticationInstance.handleRegister({
      username: state.username,
      email: state.email,
      password: state.password,
      firstName: humanparser.parseName(state.name).firstName as string,
      lastName: humanparser.parseName(state.name).lastName as string,
      gender: state.gender as string,
      dateOfBirth: (state.dob as DateTime).toISODate() as string,
      referralCode,
      experienceId: experienceId,
      agreedToMarketing: state.agreedToMarketing,
    })
      .then(ok)
      .catch((err: Error) => {
        showError(err.message);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <DialogHeader
        title="Create Your Account"
        subheader={
          <Link
            onClick={handleLogin}
            sx={{
              color: "inherit",
              textDecoration: "underline",
              "&:hover": {
                cursor: "pointer",
              },
            }}
          >
            Already have an account? Sign In
          </Link>
        }
      />
      <CardContent
        sx={{
          overflowY: "scroll",
          p: {
            xs: 1,
            sm: 2,
          },
        }}
      >
        <TextField
          fullWidth
          size="small"
          label="Email"
          margin="dense"
          name="email"
          type="email"
          value={state.email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!errors?.email}
          helperText={errors?.email || ""}
          autoComplete="email"
          variant="outlined"
        />
        <TextField
          fullWidth
          size="small"
          label="Username"
          margin="dense"
          name="username"
          value={state.username}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!errors?.username}
          helperText={errors?.username || ""}
          autoComplete="username"
          variant="outlined"
        />

        <Divider sx={{ opacity: 1, my: 1 }} />
        <TextField
          size="small"
          fullWidth
          label="Full Name"
          margin="dense"
          name="name"
          value={state.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!errors?.name}
          helperText={errors?.name || ""}
          autoComplete="name"
          variant="outlined"
        />
        <FormControl fullWidth variant="outlined" margin="dense" size="small">
          <InputLabel id="gender-select-label" margin="dense">
            Gender
          </InputLabel>
          <Select
            labelId="gender-select-label"
            id="gender-select"
            value={state?.gender || ""}
            label="Gender"
            size="small"
            name={"gender"}
            onChange={handleChange}
          >
            {GENDER_OPTIONS.map((opt, idx) => (
              <MenuItem key={idx} value={opt} sx={{ textTransform: "capitalize" }}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <DatePicker
          label={"Date of Birth"}
          value={state.dob}
          onChange={(dob: DateTime | null) => {
            setState({ ...state, dob });
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              name: "dob",
              onBlur: handleBlur,
              error: !!errors?.dob,
              size: "small",
              margin: "dense",
              helperText: errors?.dob || "You must be at least 21 years old to register",
            },
          }}
          disableFuture
        />
        <Divider sx={{ opacity: 1, my: 1 }} />
        <TextField
          fullWidth
          size="small"
          label="Password"
          margin="dense"
          name="password"
          value={state.password}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!errors?.password}
          helperText={errors?.password || "Password must be at least 8 characters and contain a special character"}
          autoComplete="new-password"
          type="password"
          variant="outlined"
        />
        <TextField
          fullWidth
          size="small"
          label="Confirm Password"
          margin="dense"
          name="confirmPassword"
          value={state.confirmPassword}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!errors?.confirmPassword}
          helperText={errors?.confirmPassword || ""}
          autoComplete="new-password-confirm"
          type="password"
          variant="outlined"
        />

        <FormControl margin="dense" fullWidth error={!!errors?.agree}>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={state.agree} name={"agree"} onChange={handleChange} />}
            slotProps={{ typography: { sx: { fontSize: 13 } } }}
            label={
              <>
                I agree to the{" "}
                <Link href="https://canna42.com/terms-of-service" target="_blank">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link href="https://canna42-privacy.netlify.app/" target="_blank">
                  Privacy Policy
                </Link>
              </>
            }
          />
        </FormControl>
        <FormControl margin="dense" fullWidth error={!!errors?.agree}>
          <FormControlLabel
            sx={{ mt: 1 }}
            slotProps={{ typography: { sx: { fontSize: 13 } } }}
            control={<Checkbox checked={state.agreedToMarketing} name={"agreedToMarketing"} onChange={handleChange} />}
            label="I wish to sign up for marketing emails and texts from Canna42 to stay in the loop of what's happening near me."
          />
        </FormControl>
      </CardContent>
      <CardActions sx={{ justifyContent: "space-between" }}>
        <Button sx={{ color: "text.secondary" }} variant="text" onClick={cancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isValid || loading}
          onClick={handleSubmit}
          startIcon={loading ? <CircularProgress sx={{ color: "inherit" }} size={20} /> : null}
        >
          Register
        </Button>
      </CardActions>
    </>
  );
};
